/* eslint-disable @typescript-eslint/no-explicit-any */
// Demo mock for bw-accounting-attributes: the company "Účetnictví" code lists
// (cost centres, document series, contracts, activities). Seeded per company,
// editable, persisted in sessionStorage alongside the payables demo state.
import { companyFromRequest } from "./payablesMock.ts";
import { DEMO_COMPANIES, DEMO_COMPANY_BY_ID } from "./seed/companies.ts";
import type { DemoCompanyId } from "./seed/identifiers.ts";

type AttributeType = "CostCenter" | "Contract" | "Activity" | "DocumentSeries";
type AttributeStatus = "Active" | "Inactive";
type Attr = {
  id: string;
  companyId: DemoCompanyId;
  attributeType: AttributeType;
  code: string;
  name: string;
  description?: string;
  status: AttributeStatus;
  ts: number;
};

const STORAGE_KEY = "fidoo-demo-accounting-attributes-v1";

const SEED: Array<Omit<Attr, "id" | "companyId" | "ts">> = [
  { attributeType: "CostCenter", code: "NS100", name: "Vedení společnosti", status: "Active" },
  { attributeType: "CostCenter", code: "NS200", name: "Obchod a marketing", status: "Active" },
  { attributeType: "CostCenter", code: "NS300", name: "Vývoj produktu", description: "Engineering, design a produktový tým", status: "Active" },
  { attributeType: "CostCenter", code: "NS400", name: "Provoz a IT", status: "Active" },
  { attributeType: "CostCenter", code: "NS900", name: "Původní pobočka Brno", description: "Uzavřeno k 31. 12. 2025", status: "Inactive" },
  { attributeType: "DocumentSeries", code: "FP2026", name: "Přijaté faktury 2026", status: "Active" },
  { attributeType: "DocumentSeries", code: "ZF2026", name: "Přijaté zálohové faktury 2026", status: "Active" },
  { attributeType: "DocumentSeries", code: "FP2025", name: "Přijaté faktury 2025", status: "Inactive" },
  { attributeType: "Contract", code: "SML-2026-001", name: "Pronájem kanceláří", description: "Nájemní smlouva na sídlo společnosti", status: "Active" },
  { attributeType: "Contract", code: "SML-2025-014", name: "Cloud hosting – rámcová smlouva", status: "Active" },
  { attributeType: "Contract", code: "SML-2026-007", name: "Marketingová agentura", status: "Active" },
  { attributeType: "Activity", code: "AKT-01", name: "Interní provoz", status: "Active" },
  { attributeType: "Activity", code: "AKT-02", name: "Zákaznický projekt Alfa", status: "Active" },
  { attributeType: "Activity", code: "AKT-03", name: "Veletrhy a eventy", status: "Active" },
];

let store: Attr[] | null = null;

function load(): Attr[] {
  if (store) return store;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      store = JSON.parse(raw) as Attr[];
      return store;
    }
  } catch {
    // Storage unavailable (private mode) — fall back to fresh seeds.
  }
  const ts = Math.floor(Date.now() / 1000);
  store = DEMO_COMPANIES.flatMap((c) =>
    SEED.map((s, i) => ({ ...s, id: `demo-acc-attr-${c.id}-${i + 1}`, companyId: c.id, ts })),
  );
  return store;
}

function save() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

function header(type: string, context = "ReqUpdateAccountingAttribute") {
  return { entityType: type, definitionVersion: "1", entityContext: context };
}

function toDoc(a: Attr): Record<string, unknown> {
  const company = DEMO_COMPANY_BY_ID[a.companyId];
  const party = { roleName: company?.name ?? "", roleType: "Client" };
  return {
    documentType: "LiveAccountingAttribute",
    documentHeader: {
      id: a.id,
      documentType: "LiveAccountingAttribute",
      definitionVersion: "1",
      _ts: a.ts,
      _etag: `"demo-${a.ts}"`,
    },
    availableActions: ["CreateDocumentReqUpdateAccountingAttribute"],
    attributeBasicInfo: {
      entityData: {
        attributeType: a.attributeType,
        name: a.name,
        code: a.code,
        status: a.status,
        ...(a.description ? { description: a.description } : {}),
      },
      entityHeader: header("attributeBasicInfo"),
      availableActions: ["this.updateEntity"],
    },
    partyRoleRef: {
      entityData: { documentId: a.companyId, documentType: "LiveCompany" },
      entityHeader: header("PartyRoleRef"),
    },
    partyRoleAdditionalInfo: {
      entityData: party,
      entityHeader: header("PartyRoleAdditionalInfo"),
    },
  };
}

function companyOf(body: Record<string, any>): DemoCompanyId {
  return companyFromRequest(body) ?? DEMO_COMPANIES[0].id;
}

function getDocumentList(body: Record<string, any>) {
  const filtering: Array<{ filteringAttribute: string; attributeValue: any }> =
    body?.filtering ?? [];
  const val = (attr: string) =>
    filtering.find((f) => f.filteringAttribute === attr)?.attributeValue;

  let items = load();
  const documentId = val("documentId");
  if (documentId) {
    items = items.filter((a) => a.id === documentId);
  } else {
    const companyId = companyOf(body);
    items = items.filter((a) => a.companyId === companyId);
  }
  const type = val("attributeType");
  if (type) items = items.filter((a) => a.attributeType === type);
  const types = val("attributeTypeArray");
  if (Array.isArray(types) && types.length) items = items.filter((a) => types.includes(a.attributeType));
  const status = val("status");
  if (status) items = items.filter((a) => a.status === status);
  const code = val("code");
  if (code) items = items.filter((a) => a.code === code);
  const search = String(val("substringSearch") ?? "").trim().toLowerCase();
  if (search) {
    items = items.filter((a) =>
      [a.code, a.name, a.description ?? ""].some((s) => s.toLowerCase().includes(search)),
    );
  }

  const sort = (Array.isArray(body?.sorting) ? body.sorting[0] : undefined) as
    | { sortingAttribute?: string; order?: string }
    | undefined;
  const key = (sort?.sortingAttribute ?? "code") as keyof Attr;
  const dir = sort?.order === "DESC" ? -1 : 1;
  items = [...items].sort((a, b) => String(a[key] ?? "").localeCompare(String(b[key] ?? ""), "cs") * dir);

  const p = body?.paging ?? {};
  const pageSize = Number(p.pageSize ?? 20);
  const start = p.startWith != null ? Number(p.startWith) : (Number(p.pageNumber ?? 1) - 1) * pageSize;
  return {
    documentList: items.slice(start, start + pageSize).map(toDoc),
    pagingResponse: {
      totalItemsCount: items.length,
      hasNextPage: start + pageSize < items.length,
      startWith: start,
      pageSize,
    },
  };
}

function createDocument(body: Record<string, any>) {
  const info = body?.entityData?.attributeBasicInfo ?? {};
  const all = load();
  const ts = Math.floor(Date.now() / 1000);
  let target: Attr | undefined;

  if (body?.documentType === "ReqUpdateAccountingAttribute") {
    const id = body?.masterDocumentReference?.documentId;
    target = all.find((a) => a.id === id);
    if (target) {
      target.name = info.name ?? target.name;
      target.code = info.code ?? target.code;
      target.status = info.status ?? target.status;
      target.description = info.description || undefined;
      target.ts = ts;
    }
  } else {
    target = {
      id: `demo-acc-attr-${Date.now().toString(36)}`,
      companyId: companyOf(body),
      attributeType: info.attributeType ?? "CostCenter",
      code: info.code ?? "",
      name: info.name ?? "",
      description: info.description || undefined,
      status: info.status ?? "Active",
      ts,
    };
    all.push(target);
  }
  save();

  const docType = body?.documentType ?? "ReqCreateAccountingAttribute";
  return {
    documentType: docType,
    documentHeader: {
      id: `${target?.id ?? "demo"}-req-${ts}`,
      documentType: docType,
      definitionVersion: "1",
      _ts: ts,
      _etag: `"demo-${ts}"`,
    },
    attributeBasicInfo: {
      entityData: target
        ? { attributeType: target.attributeType, name: target.name, code: target.code, status: target.status }
        : info,
      entityHeader: header("attributeBasicInfo", docType),
    },
  };
}

type Handler = (req: { body: Record<string, unknown>; method: string }) => unknown;
const copy = (fn: (body: Record<string, any>) => unknown): Handler => (req) =>
  JSON.parse(JSON.stringify(fn(req.body ?? {})));

export const ACCOUNTING_ATTRIBUTES_HANDLERS: Record<string, Handler> = {
  "bw-accounting-attributes/getDocumentList": copy(getDocumentList),
  "bw-accounting-attributes/readEntities": copy((body) => {
    const id = body?.documentReference?.documentId ?? body?.documentId;
    const hit = load().find((a) => a.id === id);
    return hit ? toDoc(hit) : {};
  }),
  "bw-accounting-attributes/createDocument": copy(createDocument),
};
